/**
 * FlowArrow Component
 *
 * Simple SVG connector arrows between flow diagram sections.
 */

import { clsx } from 'clsx';

interface FlowArrowProps {
  direction?: 'down' | 'right';
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  color?: string;
  dashed?: boolean;
}

const SIZE_CONFIG = {
  sm: { width: 24, height: 24, stroke: 1.5 },
  md: { width: 32, height: 32, stroke: 2 },
  lg: { width: 48, height: 48, stroke: 2.5 },
};

export function FlowArrow({
  direction = 'down',
  label,
  size = 'md',
  color = 'text-gray-400',
  dashed = false,
}: FlowArrowProps) {
  const config = SIZE_CONFIG[size];

  return (
    <div className={clsx('flex items-center justify-center', direction === 'down' ? 'flex-col' : 'flex-row')}>
      <svg
        width={config.width}
        height={config.height}
        viewBox="0 0 24 24"
        fill="none"
        className={color}
        style={{
          transform: direction === 'right' ? 'rotate(-90deg)' : 'none',
        }}
      >
        <path
          d="M12 5v14M12 19l-4-4M12 19l4-4"
          stroke="currentColor"
          strokeWidth={config.stroke}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={dashed ? '4 2' : 'none'}
        />
      </svg>
      {label && (
        <span className="text-xs text-gray-500 ml-2">{label}</span>
      )}
    </div>
  );
}

/**
 * Vertical connector with optional label (used between rounds)
 */
export function VerticalConnector({
  label,
  highlighted = false,
}: {
  label?: string;
  highlighted?: boolean;
}) {
  return (
    <div className="flex flex-col items-center py-2">
      <div
        className={clsx(
          'w-0.5 h-6',
          highlighted ? 'bg-indigo-400' : 'bg-gray-300'
        )}
      />
      {label && (
        <span
          className={clsx(
            'px-2 py-0.5 text-xs font-medium rounded-full my-1',
            highlighted
              ? 'bg-indigo-100 text-indigo-700'
              : 'bg-gray-100 text-gray-600'
          )}
        >
          {label}
        </span>
      )}
      <div
        className={clsx(
          'w-0.5 h-6',
          highlighted ? 'bg-indigo-400' : 'bg-gray-300'
        )}
      />
      {/* Arrow head */}
      <svg
        width="12"
        height="8"
        viewBox="0 0 12 8"
        className={highlighted ? 'text-indigo-400' : 'text-gray-300'}
      >
        <path
          d="M6 8L0 0h12L6 8z"
          fill="currentColor"
        />
      </svg>
    </div>
  );
}

/**
 * Horizontal branching connector for parallel contributions
 */
export function BranchConnector({ count }: { count: number }) {
  if (count <= 1) return null;

  return (
    <div className="relative w-full h-8 flex items-center justify-center">
      {/* Horizontal line */}
      <div className="absolute left-1/4 right-1/4 top-0 h-0.5 bg-gray-300" />

      {/* Vertical drops */}
      <div className="flex w-full justify-around">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="flex flex-col items-center">
            <div className="w-0.5 h-4 bg-gray-300" />
            <svg width="8" height="6" viewBox="0 0 8 6" className="text-gray-300">
              <path d="M4 6L0 0h8L4 6z" fill="currentColor" />
            </svg>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Merge connector for converging branches
 */
export function MergeConnector({ count }: { count: number }) {
  if (count <= 1) return null;

  return (
    <div className="relative w-full h-8 flex items-center justify-center">
      {/* Vertical rises */}
      <div className="flex w-full justify-around">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="w-0.5 h-4 bg-gray-300" />
        ))}
      </div>

      {/* Horizontal line */}
      <div className="absolute left-1/4 right-1/4 bottom-4 h-0.5 bg-gray-300" />

      {/* Center drop */}
      <div className="absolute bottom-0 flex flex-col items-center">
        <div className="w-0.5 h-4 bg-gray-300" />
        <svg width="8" height="6" viewBox="0 0 8 6" className="text-gray-300">
          <path d="M4 6L0 0h8L4 6z" fill="currentColor" />
        </svg>
      </div>
    </div>
  );
}
